import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { userOrganizationsQueryOptions } from "@/hooks/use-organizations";
import { api } from "@/lib/api";
import OrganizationPage from "@/pages/admin/organization";
import type { Session } from "@/types/session";

const organizationSessionsQueryOptions = (organization_id: string) =>
  queryOptions<Session[]>({
    queryKey: ["organization", "sessions", organization_id],
    meta: {
      url: "/session",
    },
  });

export const Route = createFileRoute("/admin/$organization_id")({
  component: RouteComponent,
  beforeLoad: async ({ context: { queryClient }, params: { organization_id } }) => {
    const orgData = localStorage.getItem("elara-org");
    const organization = orgData ? JSON.parse(orgData) : null;
    const organizations = await queryClient.ensureQueryData(userOrganizationsQueryOptions);

    // A stale selection (e.g. an organization that no longer exists or the user
    // lost access to) must be cleared, or the backend rejects every request.
    if (
      organization?.id !== organization_id ||
      !organizations.some((org) => org.id === organization_id)
    ) {
      localStorage.removeItem("elara-org");
      throw redirect({
        to: "/admin",
        replace: true,
      });
    }

    // The backend reads the organization from the elara-org cookie, which can be
    // gone while localStorage still holds the selection; re-sync it.
    await api.post("/set-organization", { id: organization_id });
  },
  loader: ({ context: { queryClient }, params: { organization_id } }) => {
    return queryClient.ensureQueryData(organizationSessionsQueryOptions(organization_id));
  },
});

function RouteComponent() {
  const { organization_id } = Route.useParams();
  const { data: sessions } = useSuspenseQuery<Session[]>(
    organizationSessionsQueryOptions(organization_id),
  );
  return <OrganizationPage sessions={sessions} />;
}
